import { Injectable } from '@angular/core';
import { Keybinding, ViewModel } from '@farris/devkit';
import * as Mousetrap from 'mousetrap';
import { Observable } from 'rxjs';

const enum CharCode {

  Digit0 = 48,
  Digit1 = 49,
  Digit2 = 50,
  Digit3 = 51,
  Digit4 = 52,
  Digit5 = 53,
  Digit6 = 54,
  Digit7 = 55,
  Digit8 = 56,
  Digit9 = 57,

  A = 65,
  B = 66,
  C = 67,
  D = 68,
  E = 69,
  F = 70,
  G = 71,
  H = 72,
  I = 73,
  J = 74,
  K = 75,
  L = 76,
  M = 77,
  N = 78,
  O = 79,
  P = 80,
  Q = 81,
  R = 82,
  S = 83,
  T = 84,
  U = 85,
  V = 86,
  W = 87,
  X = 88,
  Y = 89,
  Z = 90,

  a = 97,
  b = 98,
  c = 99,
  d = 100,
  e = 101,
  f = 102,
  g = 103,
  h = 104,
  i = 105,
  j = 106,
  k = 107,
  l = 108,
  m = 109,
  n = 110,
  o = 111,
  p = 112,
  q = 113,
  r = 114,
  s = 115,
  t = 116,
  u = 117,
  v = 118,
  w = 119,
  x = 120,
  y = 121,
  z = 122
}

/**
 * 快捷键服务
 * @scope FormModule
 */
@Injectable()
export class KeybindingService {

  private keyMap: Map<string, () => Observable<any>>;

  private bindingProvider: Mousetrap;

  private ready: boolean;

  constructor() {
    this.keyMap = new Map<string, () => Observable<any>>();
    this.bindingProvider = Mousetrap.default;
    this.ready = true;
  }

  /**
   * 对视图模型设置的快捷键进行绑定处理
   * @param viewModel 视图模型
   */
  public bind(viewModel: ViewModel) {
    viewModel.keybindingMap.forEach((keyBinding, method) => {
      this.register(keyBinding, () => {
        return viewModel[method]();
      });
    });
  }

  /**
   * 注册快捷键
   * @param binding 键盘绑定信息
   * @param handler 响应事件
   */
  public register(binding: Keybinding, handler: () => Observable<any>) {
    const combo = this.getCombo(binding);
    if (combo) {
      this.keyMap.set(combo, handler);
      if (binding.ctrlKey && binding.altKey && !binding.shiftKey) {
        // 实际发现，ctrl+alt组合，只能触发keyup事件
        this.bindingProvider.bind(combo, this.dispatch.bind(this), 'keyup');
      } else {
        this.bindingProvider.bind(combo, this.dispatch.bind(this));
      }
    }
  }

  /**
   * 取消快捷键注册
   * @param binding 键盘绑定信息
   */
  public unregister(binding: Keybinding) {
    const combo = this.getCombo(binding);
    if (combo) {
      this.keyMap.delete(combo);
      this.bindingProvider.unbind(combo);
    }
  }

  private dispatch(e: KeyboardEvent): boolean {
    if (e.repeat) {
      return false;
    }
    if (this.ready) {
      const combo = this.getCombo(e);
      if (combo && this.keyMap.has(combo)) {
        this.ready = false;
        this.keyMap.get(combo)().subscribe(() => {
          this.ready = true;
        }, (error) => {
          this.ready = true;
        }, () => {
          this.ready = true;
        });
      }
    }
    return false;
  }

  /**
   * 返回ctrl+shift+alt+a形式的组合字符串，全部为小写
   * @param keyInfo 键盘绑定信息
   */
  private getCombo(keyInfo: Keybinding | KeyboardEvent): string | null {
    const key = keyInfo.key.toLowerCase();
    if (key.length !== 1 || key.charCodeAt(0) < CharCode.a || key.charCodeAt(0) > CharCode.z) {
      console.warn('快捷键字母形式为a-z');
      return null;
    }
    const combo = (keyInfo.ctrlKey ? 'ctrl+' : '')
      + (keyInfo.shiftKey ? 'shift+' : '')
      + (keyInfo.altKey ? 'alt+' : '')
      + (keyInfo.metaKey ? 'meta+' : '')
      + key;

    if (combo.length === 1) {
      console.warn('快捷键至少包含一个Modifier键');
      return null;
    }
    return combo;
  }
}
